import { Card, CardContent, CardHeader, CardTitle } from "@nimit9/signet-ui/components/card"
import { Input } from "@nimit9/signet-ui/components/input"
import { PillGroup } from "@nimit9/signet-ui/components/pill-group"
import { Switch } from "@nimit9/signet-ui/components/switch"
import {
  ADVANCE_TAX_THRESHOLD,
  capitalGainsTax,
  deductions,
  EMPTY_INPUTS as EMPTY,
  estimateTax,
  financialYear,
  harvestHeadroom,
  LIMITS,
  type ManualInputs,
  nextAdvanceTax,
} from "@paisa/finance/tax"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { flattenCategories, useCategories } from "@/hooks/use-categories"
import { useInsurance } from "@/hooks/use-insurance"
import { useInvestments } from "@/hooks/use-investments"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { useTransactions } from "@/hooks/use-transactions"
import { api } from "@/lib/api"

export const Route = createFileRoute("/tax/")({ component: TaxPage })

// Figures Paisa can't see (TDS, employer NPS, EPF, HRA…) are kept on this device per person per FY.

function useManualInputs(owner: string, fy: string) {
  const key = `paisa_tax_${owner}_${fy}`
  const [values, setValues] = useState<ManualInputs>(EMPTY)
  useEffect(() => {
    try {
      setValues({ ...EMPTY, ...JSON.parse(localStorage.getItem(key) ?? "{}") })
    } catch {
      setValues(EMPTY)
    }
  }, [key])
  function update(patch: Partial<ManualInputs>) {
    setValues((prev) => {
      const next = { ...prev, ...patch }
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        // storage unavailable: values last for this visit only
      }
      return next
    })
  }
  return [values, update] as const
}

function MoneyField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  hint?: string
}) {
  const id = `tax-${label.replace(/\W+/g, "-").toLowerCase()}`
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-caption text-text-secondary">
        {label}
      </label>
      <Input
        id={id}
        inputMode="numeric"
        className="h-11 md:h-9"
        value={value ? String(value) : ""}
        placeholder="0"
        onChange={(e) => onChange(Number(e.target.value.replace(/[^\d]/g, "")) || 0)}
      />
      {hint && <p className="text-caption text-text-muted">{hint}</p>}
    </div>
  )
}

function Row({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className={muted ? "text-caption text-text-muted" : "text-body text-text-secondary"}>
        {label}
      </span>
      <Amount value={value} className="tabular-nums" />
    </div>
  )
}

function TaxPage() {
  const fy = financialYear()
  const ownerLabels = useOwnerLabels()
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api.get<{ data: { owner: string | null } }>("/profile"),
  })
  const owners = Object.keys(ownerLabels).filter((o) => o !== "joint")
  const [picked, setPicked] = useState<string | null>(null)
  const owner = picked ?? profile?.data?.owner ?? owners[0] ?? "user_1"
  const [compareOld, setCompareOld] = useState(false)
  const [manual, setManual] = useManualInputs(owner, fy.label)

  const { data: categories } = useCategories()
  const salaryCategoryId = useMemo(
    () => flattenCategories(categories?.data ?? []).find((c) => c.slug === "salary")?.id,
    [categories],
  )
  const { data: salaryData, isLoading: salaryLoading } = useTransactions(
    {
      categoryId: salaryCategoryId,
      owner,
      startDate: fy.start,
      endDate: fy.end,
      type: "credit",
      page: 1,
      pageSize: 1,
    },
    { enabled: !!salaryCategoryId },
  )
  const salaryYtd = salaryData?.summary?.income ?? 0

  const { data: investments } = useInvestments()
  const { data: insurance } = useInsurance()

  // 80C/80D from what Paisa already knows, plus what was typed in.
  const elss = investments?.tax?.section80c?.elss ?? 0
  const { lifePremiums, healthPremiums, sec80C } = deductions(
    owner,
    insurance?.insurance ?? [],
    manual,
    elss,
  )

  const estimate = estimateTax({
    salaryYtd,
    monthsElapsed: fy.monthsElapsed,
    employerNps: manual.employerNps,
    tdsPaid: manual.tdsPaid,
    sec80C,
    sec80D: healthPremiums,
    sec80CCD1B: manual.nps1b,
    hra: manual.hra,
  })

  // Capital gains: unrealised gains on what's held today (household-wide).
  const ltcg = investments?.tax?.totalLtcg ?? 0
  const stcg = investments?.tax?.totalStcg ?? 0
  const headroom = harvestHeadroom(0)
  const advance = nextAdvanceTax()
  const paidShare = estimate.newTax > 0 ? Math.min(1, manual.tdsPaid / estimate.newTax) : 1

  return (
    <>
      <Header title="Tax" showPeriod={false} />
      <PageWrapper>
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-caption text-text-muted">FY {fy.label} · estimate, not tax advice</p>
            {owners.length > 1 && (
              <PillGroup
                className="[&>button]:min-h-11 md:[&>button]:min-h-0"
                options={owners.map((o) => ({ value: o, label: ownerLabels[o] }))}
                value={owner}
                onChange={setPicked}
              />
            )}
          </div>

          <Card data-testid="tax-estimate">
            <CardHeader>
              <CardTitle>Estimated tax (new regime)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="text-display font-semibold tabular-nums">
                <Amount value={estimate.newTax} />
              </div>
              <div
                className="h-2 overflow-hidden rounded-full bg-bg-elevated"
                role="img"
                aria-label={`${Math.round(paidShare * 100)}% already paid through TDS`}
              >
                <div
                  className="h-full bg-accent-green"
                  style={{ width: `${Math.round(paidShare * 100)}%` }}
                />
              </div>
              <Row label="Salary this year (projected)" value={estimate.projectedSalary} />
              <Row
                label={`Salary credited so far (${fy.monthsElapsed} of 12 months)`}
                value={salaryYtd}
                muted
              />
              <Row label="TDS paid so far" value={manual.tdsPaid} muted />
              <Row label="Left to pay after TDS" value={estimate.dueAfterTds} />
              {!salaryLoading && salaryYtd === 0 && (
                <p className="text-caption text-text-muted">
                  No salary credits this year yet. Categorise salary as "Salary" to estimate.
                </p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="tax-gains">
            <CardHeader>
              <CardTitle>Capital gains</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Row label="Long-term equity gains on holdings" value={ltcg} />
              <Row label="Short-term equity gains on holdings" value={stcg} />
              <Row label="Tax if all sold today" value={capitalGainsTax(ltcg, stcg)} muted />
              {ltcg > 0 && (
                <p className="text-body text-text-primary">
                  You can book up to <Amount value={Math.min(ltcg, headroom)} /> of long-term gains
                  tax-free before 31 March (₹1.25L a year, if you haven't sold any this year).
                </p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="tax-upcoming">
            <CardHeader>
              <CardTitle>Upcoming</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-body text-text-secondary">
              {estimate.dueAfterTds >= ADVANCE_TAX_THRESHOLD ? (
                <p>
                  {advance.date.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}:
                  advance tax, {Math.round(advance.share * 100)}% of the year's tax due by then
                </p>
              ) : (
                <p>No advance tax needed: TDS covers all but ₹10,000 or less.</p>
              )}
              <p>31 Jul: file your return (ITR)</p>
            </CardContent>
          </Card>

          <Card data-testid="tax-inputs">
            <CardHeader>
              <CardTitle>What Paisa can't see</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <MoneyField
                label="TDS paid this year"
                value={manual.tdsPaid}
                onChange={(v) => setManual({ tdsPaid: v })}
                hint="From your payslips or Form 26AS"
              />
              <MoneyField
                label="Employer NPS this year"
                value={manual.employerNps}
                onChange={(v) => setManual({ employerNps: v })}
                hint="80CCD(2): counts in both regimes"
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-between rounded-xl border border-border-subtle px-5 py-3">
            <label htmlFor="compare-old" className="text-body text-text-primary">
              Compare with the old regime
            </label>
            <Switch id="compare-old" checked={compareOld} onCheckedChange={setCompareOld} />
          </div>

          {compareOld && (
            <Card data-testid="tax-old-regime">
              <CardHeader>
                <CardTitle>
                  {estimate.better === "new" ? "New regime saves " : "Old regime saves "}
                  <Amount value={estimate.saving} />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Row label="Old regime" value={estimate.oldTax} />
                <Row label="New regime" value={estimate.newTax} />
                <Row
                  label={`80C (cap ₹1.5L): ELSS ${Math.round(elss)}, life cover ${Math.round(lifePremiums)}`}
                  value={Math.min(sec80C, LIMITS.sec80C)}
                  muted
                />
                <Row label="80D health premiums (cap ₹25k)" value={healthPremiums} muted />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <MoneyField
                    label="EPF (your share)"
                    value={manual.epf}
                    onChange={(v) => setManual({ epf: v })}
                  />
                  <MoneyField
                    label="PPF"
                    value={manual.ppf}
                    onChange={(v) => setManual({ ppf: v })}
                  />
                  <MoneyField
                    label="NPS 80CCD(1B)"
                    value={manual.nps1b}
                    onChange={(v) => setManual({ nps1b: v })}
                    hint="Up to ₹50,000"
                  />
                  <MoneyField
                    label="HRA exemption"
                    value={manual.hra}
                    onChange={(v) => setManual({ hra: v })}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </PageWrapper>
    </>
  )
}
